import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import { LockOutlined, MailOutline, Visibility, VisibilityOff } from "@mui/icons-material";
import { auth, googleProvider, microsoftProvider, signInWithPopup } from "../firebase/firebaseConfig";
import { signInWithEmailAndPassword, sendEmailVerification, fetchSignInMethodsForEmail } from "firebase/auth";
import { authErrorText, backendLogin, existingMethodText } from "../firebase/authErrors";
import AuthLayout, { AuthTitle, SubmitLabel, fieldSx, helperTextSx, mutedTextSx, oauthButtonSx, submitSx } from "../components/AuthLayout";
import { colors } from "../styles/tokens";
import OrgAdminSignIn from "./OrgLicensing/OrgAdminSignIn";
import { getLicSession } from "./OrgLicensing/licApi";

// /login?as=it-admin opens the IT admin (org licensing) sign-in, ?as=user the end-user sign-in. Without `as`, the
// side this browser last signed in on is shown.
const USER = "user";
const IT_ADMIN = "it-admin";
const MODE_KEY = "loginMode";

const MODE_TEXT = {
  [USER]: "Welcome back! For individuals and company team members.",
  [IT_ADMIN]: "For people who manage their company's HUBAROC licenses. We'll email you a 6-digit code.",
};

const readSavedMode = () => {
  try {
    return localStorage.getItem(MODE_KEY) === IT_ADMIN ? IT_ADMIN : USER;
  } catch {
    return USER;
  }
};

const saveMode = (mode) => {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    // Storage unavailable (private window): the side just isn't remembered.
  }
};

const getStoredToken = () => localStorage.getItem("jwttoken") || sessionStorage.getItem("jwttoken");

const modeToggleSx = {
  mt: 2.5,
  "& .MuiToggleButton-root": {
    flex: 1,
    py: 1,
    fontWeight: 500,
    fontSize: "15px",
    color: colors.muted,
    borderColor: colors.inputBorder,
    borderRadius: "10px",
  },
  "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover": {
    backgroundColor: colors.beige,
    color: colors.goldText,
    fontWeight: 600,
  },
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [savedMode] = useState(readSavedMode);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [busy, setBusy] = useState(null); // "password", "Google" or "Microsoft" while that sign-in is running
  const [message, setMessage] = useState(null); // { severity, text, canResend }
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  const [showDisplayNameForm, setShowDisplayNameForm] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");

  const successMessage = location.state?.message || "";
  const asParam = searchParams.get("as");
  const mode = asParam === IT_ADMIN || asParam === USER ? asParam : savedMode;

  useEffect(() => {
    // Already signed in for the selected kind of account: skip the form.
    if (mode === IT_ADMIN) {
      if (getLicSession()) navigate("/org-licensing", { replace: true });
      return;
    }
    if (getStoredToken()) {
      navigate("/dashboard");
    }
  }, [navigate, mode]);

  const handleModeChange = (event, nextMode) => {
    if (!nextMode) return;
    setMessage(null);
    setSearchParams({ as: nextMode }, { replace: true });
  };

  const showError = (error) => {
    const text = error?.status ? "We couldn't sign you in. Please try again, or contact support if it keeps happening." : authErrorText(error);
    setMessage(text ? { severity: "error", text } : null);
  };

  const storeSession = ({ token, user, email }) => {
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem("jwttoken", token);
    storage.setItem("user", user);
    storage.setItem("email", email);
    saveMode(USER);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = data.get("email");
    const password = data.get("password");

    setMessage(null);
    setBusy("password");
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        setUnverifiedUser(user);
        setMessage({
          severity: "warning",
          text: "Please verify your email before logging in. Open the link we emailed you, then log in again.",
          canResend: true,
        });
        return;
      }

      const idToken = await user.getIdToken();
      const json = await backendLogin("/api/users/firebase-login", idToken);
      storeSession({
        token: json.token || idToken,
        user: json.displayName || user.displayName || "Anonymous",
        email: json.email || user.email,
      });
      navigate(`/dashboard`);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedUser) return;
    try {
      await sendEmailVerification(unverifiedUser);
      setMessage({
        severity: "success",
        text: `Verification email sent to ${unverifiedUser.email}. Check your inbox, then log in.`,
      });
    } catch (error) {
      showError(error);
    }
  };

  const handleOAuthSignIn = async (provider, providerName) => {
    setMessage(null);
    setBusy(providerName);
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await user.getIdToken();
      const json = await backendLogin("/api/users/oauth-login", idToken);
      storeSession({
        token: json.token,
        user: json.displayName || user.displayName || "Anonymous",
        email: json.email,
      });
      const displayName = json.displayName;
      if (!displayName || displayName.trim() === "") {
        setShowDisplayNameForm(true);
      } else {
        navigate(`/dashboard`);
      }
    } catch (error) {
      if (error.code === "auth/account-exists-with-different-credential") {
        let methods = [];
        try {
          methods = await fetchSignInMethodsForEmail(auth, error.customData.email);
        } catch {
          // Fall back to the general wording below.
        }
        setMessage({
          severity: "error",
          text: `An account with this email already exists. Please sign in with ${existingMethodText(methods)}.`,
        });
      } else {
        showError(error);
      }
    } finally {
      setBusy(null);
    }
  };

  const handleDisplayNameSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setBusy("displayName");
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/userdata/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getStoredToken()}`,
        },
        body: JSON.stringify({ name: newDisplayName, companyname: "" }),
      });

      if (response.ok) {
        const storage = localStorage.getItem("jwttoken") ? localStorage : sessionStorage;
        storage.setItem("user", newDisplayName);
        navigate(`/dashboard`);
      } else {
        setMessage({ severity: "error", text: "We couldn't save your display name. Please try again." });
      }
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  };

  const banner = (
    <Box
      sx={{
        backgroundColor: colors.black,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        padding: "12px 20px",
        width: "100%",
      }}
    >
      <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#FFFFFF" }}>
        BAMROC is live!
      </Typography>
      <Button variant="contained" href="https://vavetek.ai/contact-us/" target="_blank" rel="noopener">
        Request Demo
      </Button>
    </Box>
  );

  return (
    <AuthLayout aside={banner}>
      <AuthTitle lead="Log in to" accent="VAVETEK.AI" />

      {successMessage && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {successMessage}
        </Alert>
      )}

      {!showDisplayNameForm && (
        <>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={mode}
            onChange={handleModeChange}
            aria-label="Account type"
            sx={modeToggleSx}
          >
            <ToggleButton value={USER}>User</ToggleButton>
            <ToggleButton value={IT_ADMIN}>IT admin</ToggleButton>
          </ToggleButtonGroup>
          <Typography sx={{ ...mutedTextSx, mt: 1.5, mb: 3, fontSize: "14px" }}>
            {MODE_TEXT[mode]}
          </Typography>
        </>
      )}

      {message && (
        <Alert
          severity={message.severity}
          sx={{ mb: 3, textAlign: "left" }}
          action={
            message.canResend && (
              <Button color="inherit" size="small" onClick={handleResendVerification}>
                Resend email
              </Button>
            )
          }
        >
          {message.text}
        </Alert>
      )}

      {mode === IT_ADMIN && !showDisplayNameForm ? (
        <OrgAdminSignIn onSignedIn={() => {
          saveMode(IT_ADMIN);
          navigate("/org-licensing", { replace: true });
        }} />
      ) : !showDisplayNameForm ? (
        <>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
            <Button
              onClick={() => handleOAuthSignIn(googleProvider, "Google")}
              disabled={Boolean(busy)}
              variant="outlined"
              size="large"
              startIcon={busy === "Google" ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon sx={{ color: "inherit" }} />}
              sx={oauthButtonSx}
            >
              Google
            </Button>
            <Button
              onClick={() => handleOAuthSignIn(microsoftProvider, "Microsoft")}
              disabled={Boolean(busy)}
              variant="outlined"
              size="large"
              startIcon={busy === "Microsoft" ? <CircularProgress size={20} color="inherit" /> : <MicrosoftIcon sx={{ color: "inherit" }} />}
              sx={oauthButtonSx}
            >
              Microsoft
            </Button>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 3, width: "100%", my: 2.5 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography sx={{ color: colors.muted, fontSize: "13px" }}>or continue with email</Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              name="email"
              type="email"
              placeholder="Email"
              autoComplete="email"
              required
              sx={fieldSx}
              inputProps={{ "aria-label": "Email" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutline sx={{ color: colors.muted }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              autoComplete="current-password"
              required
              sx={fieldSx}
              inputProps={{ "aria-label": "Password" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: colors.muted }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <VisibilityOff sx={{ color: colors.muted }} /> : <Visibility sx={{ color: colors.muted }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box mt={1} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <FormControlLabel
                control={
                  <Checkbox checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} />
                }
                label={<Typography sx={helperTextSx}>Remember me</Typography>}
              />
              <Link href="/forgotpassword" sx={helperTextSx}>
                Forgot password?
              </Link>
            </Box>

            <Box mt={3}>
              <Button type="submit" variant="contained" size="large" fullWidth disabled={Boolean(busy)} sx={submitSx}>
                <SubmitLabel busy={busy === "password"}>Log in</SubmitLabel>
              </Button>
            </Box>

            <Typography sx={{ ...helperTextSx, mt: 3, textAlign: "center" }}>
              <span style={{ marginRight: "20px" }}>Don't have an account?</span>
              <Link href="/register">
                Create an account
              </Link>
            </Typography>
          </Box>
        </>
      ) : (
        <Box component="form" onSubmit={handleDisplayNameSubmit} sx={{ mt: 3 }}>
          <Typography sx={{ fontSize: "17px", fontWeight: 500, textAlign: "left", mb: 1 }}>
            Choose the name shown on your account to finish setting up.
          </Typography>
          <TextField
            margin="normal"
            name="displayName"
            placeholder="Display name"
            autoComplete="name"
            autoFocus
            required
            value={newDisplayName}
            onChange={(e) => setNewDisplayName(e.target.value)}
            sx={fieldSx}
            inputProps={{ "aria-label": "Display name" }}
          />
          <Box mt={3}>
            <Button type="submit" variant="contained" size="large" fullWidth disabled={Boolean(busy)} sx={submitSx}>
              <SubmitLabel busy={busy === "displayName"}>Continue</SubmitLabel>
            </Button>
          </Box>
        </Box>
      )}
    </AuthLayout>
  );
}
